import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { ClinicAuthError } from "@auth/clinicAuthClient";

type Wire = components["schemas"];

/** Mirrors brian `room_phase_rules.PhaseKind`: the room kinds that have phases. */
type PhaseKind = Wire["RoomPhaseOut"]["kind"];

/** Mirrors brian `routes/perception.py` `RoomPhaseOut`; change both together. */
/** brian `RoomOccupant`: a staff name comes from a reference photo or voice; clients and animals are tied to a visit, never named from a face. */
export interface RoomOccupant {
  /** brian's roster slot id (`p1`, `a2`), the `slot` in its `room_roster_change` logs; observations name a slot by it. */
  slot: string;
  kind: string;
  role: string;
  displayName: string | null;
  /** The staff member's user uuid once named. */
  nameRef: string | null;
  /** brian `NameStatus`: `none`, `candidate`, `supported` or `confirmed`. */
  nameStatus: string;
  /** Why the name holds; a `manual:<observation uuid>` entry means a person set it. */
  nameBasis: string[];
  visitUuid: string | null;
  /** brian `VisitStatus`: `none`, `candidate` or `bound`. */
  visitStatus: string;
  lastSeenAt: string;
  misses: number;
  /** A pet counted in a carrier, never boxed. */
  carried: boolean;
  /** It carries a role, a staff name or a visit; screens count the rest (BRI-337). */
  identified: boolean;
}

export interface RoomPhase {
  roomId: string;
  roomName: string;
  kind: PhaseKind;
  /** A brian `room_phase_rules.Phase` value, such as `PATIENT_OUT`. */
  phase: string;
  since: string;
  occupants: RoomOccupant[];
  counts: RoomCounts | null;
}

/**
 * Mirrors brian `RoomCountsOut`; `person`, `animal` and `carrier` are `0`, `1`, `2`,
 * `3_or_more` or `unclear`; `in_carrier` says `animal` is the carrier count, no animal seen.
 */
export type RoomCounts = Wire["RoomCountsOut"];

/** Mirrors brian `RoomStillOut`, which `/still` and `/recent-stills` both return. */
export interface RoomStill {
  /** Presigned for about a minute (about five from `recentStills`); brian signs it again on every read, so the same still arrives under a new URL. */
  url: string;
  /** brian's `frame:<sha>:<key>`; staff-photo capture sends it back so the crop comes from this exact still. */
  reference: string;
  capturedAt: string;
  /** The camera's source size, the pixels `camera_fixtures` are written in; null when the gateway did not record it. */
  width: number | null;
  height: number | null;
}

/** A still from `recentStills`, with when its URL was signed (ms since the epoch, the browser's clock). */
export interface RecentStill extends RoomStill {
  signedAt: number;
}

const toRoomStill = (s: Wire["RoomStillOut"]): RoomStill => ({
  url: s.url,
  reference: s.reference,
  capturedAt: s.captured_at,
  width: s.width,
  height: s.height,
});

/** brian `GET /perception/rooms/{room}/video`: an MP4 whose first frame arrived at `startedAt` (`X-Video-Start`). */
export interface RoomVideo {
  mp4: Blob;
  startedAt: string;
}

/** Mirrors brian `SlotStillOut`: `url` is presigned for about a minute, null when brian has no frames bucket. */
export type SlotStill = Wire["SlotStillOut"];

/** Mirrors brian `SlotBoxOut`: fractions of the still's width and height. */
export type SlotBox = Wire["SlotBoxOut"];

/**
 * Mirrors brian `SlotChangeOut`, a `perception_roster_changes` row; its vocabulary is
 * `room_roster_change` in brian `docs/perception.md`. A camera change's `changed_at`
 * is its tick's `ticked_at` in the Jev calls.
 */
export type SlotChange = Wire["SlotChangeOut"];

/** Mirrors brian `NameFromOut` (`room_roster_rules.NameSources`). */
export interface NameFrom {
  faces: SlotStill[];
  facesWithoutStill: number;
  voice: boolean;
  manual: boolean;
}

/** Mirrors brian `routes/perception.py` `RoomSlotOut` (BRI-354); change both together. */
export interface RoomSlot {
  occupant: RoomOccupant;
  enteredAt: string;
  headPose: string;
  /** brian `not_held`: why it would not be kept out of view; `off` while holding is switched off. */
  notHeld: Wire["RoomSlotOut"]["not_held"];
  /** For a carried pet, the person it came in with. */
  withPerson: string | null;
  nameP: number;
  nameAt: string | null;
  supportedAt: string | null;
  nameTries: number;
  nameFrom: NameFrom;
  visitAt: string | null;
  visitAskedAt: string | null;
  visitHeard: number;
  /** The still it was last matched on, at `occupant.lastSeenAt`. */
  seen: SlotStill | null;
  /** Every slot last matched on `seen`, this one included. */
  boxes: SlotBox[];
  /** Oldest first. */
  history: SlotChange[];
}

/** A brian `room_phase_rules.Phase` value. */
export type Phase = Wire["Phase"];

/** A brian `ground_truth.Crossing`. */
export type Crossing = Wire["Crossing"];

/** brian `DoorTap`: someone at one of the room's clinic-map doors. */
type DoorTap = Wire["DoorTap"];

export type GroundTruthIn = Wire["GroundTruthIn"];

/** Mirrors brian `GroundTruthOut`: what a tester saw happen in a room. Text can name clients and pets. */
export interface GroundTruthEntry {
  id: string;
  recordedAt: string;
  phase: Phase | null;
  text: string | null;
  door: DoorTap | null;
  authorName: string | null;
}

/** Mirrors brian `room_events.RoomEvent` (BRI-270): `line` is empty for a kind it does not describe. */
export type RoomEvent = Wire["RoomEvent"];

/** Mirrors brian `RoomGroundTruthOut`: the room kind's phases (the tester's buttons) and the newest entries, newest first. */
interface RoomGroundTruth {
  phases: Phase[];
  entries: GroundTruthEntry[];
}

const toOccupant = (o: Wire["RoomOccupantOut"]): RoomOccupant => ({
  slot: o.slot,
  kind: o.kind,
  role: o.role,
  displayName: o.display_name,
  nameRef: o.name_ref,
  nameStatus: o.name_status,
  nameBasis: o.name_basis,
  visitUuid: o.visit_uuid,
  visitStatus: o.visit_status,
  lastSeenAt: o.last_seen_at,
  misses: o.misses,
  carried: o.carried,
  identified: o.identified,
});

const toSlot = (s: Wire["RoomSlotOut"]): RoomSlot => ({
  occupant: toOccupant(s.occupant),
  enteredAt: s.entered_at,
  headPose: s.head_pose,
  notHeld: s.not_held,
  withPerson: s.with_person,
  nameP: s.name_p,
  nameAt: s.name_at,
  supportedAt: s.supported_at,
  nameTries: s.name_tries,
  nameFrom: {
    faces: s.name_from.faces,
    facesWithoutStill: s.name_from.faces_without_still,
    voice: s.name_from.voice,
    manual: s.name_from.manual,
  },
  visitAt: s.visit_at,
  visitAskedAt: s.visit_asked_at,
  visitHeard: s.visit_heard,
  seen: s.seen,
  boxes: s.boxes,
  history: s.history,
});

export const isManual = (occupant: RoomOccupant) =>
  occupant.nameBasis.some((basis) => basis.startsWith("manual:"));

export type SlotKind = NonNullable<Wire["ObservationIn"]["slot_kind"]>;
export type SlotRole = Wire["Role"];
/** `observation_uuid` is minted by the browser and resent unchanged on a retry: brian's idempotency key for the observation. */
export type ObservationIn = Wire["ObservationIn"];
export type ObservationsIn = Wire["ObservationsIn"];

interface ObservationResult {
  observationUuid: string;
  status: "applied" | "duplicate";
  eventUuid: string;
}

/** Mirrors brian's observations receipt. */
export interface ObservationsReceipt {
  roomId: string;
  updatedAt: string;
  occupantsChangedEventUuid: string | null;
  results: ObservationResult[];
  occupants: RoomOccupant[];
}

/**
 * brian's 409 (`payload_mismatch`, `raced`, a rule refusal) has `detail: {reason, observation_uuid?}`,
 * which `request()` drops because it is not a string, and the OpenAPI document does not declare it.
 * Its 422 `observed_at_out_of_range: <observation_uuid>` names the observation whose time is out
 * of bounds. Nothing in the request was recorded, so the same batch (and uuids) can be sent again.
 */
export class ObservationRefused extends ClinicAuthError {
  reason: string;
  observationUuid: string | null;
  constructor(reason: string, observationUuid: string | null, status = 409) {
    super("unknown", reason, status, reason);
    this.reason = reason;
    this.observationUuid = observationUuid;
    this.name = "ObservationRefused";
  }
}

const refusalOf = (error: unknown): ObservationRefused | null => {
  if (!error || typeof error !== "object" || !("detail" in error)) return null;
  const { detail } = error as { detail: unknown };
  if (typeof detail === "string") return new ObservationRefused(detail, null);
  if (!detail || typeof detail !== "object" || !("reason" in detail))
    return null;
  const { reason, observation_uuid } = detail as {
    reason: unknown;
    observation_uuid?: unknown;
  };
  return typeof reason === "string"
    ? new ObservationRefused(
        reason,
        typeof observation_uuid === "string" ? observation_uuid : null,
      )
    : null;
};

/** The 404 carries the same object detail as the 409; an older plain-string 404 stays a generic error. */
const notFoundOf = (error: unknown): ObservationRefused | null => {
  if (!error || typeof error !== "object" || !("detail" in error)) return null;
  const { detail } = error as { detail: unknown };
  if (!detail || typeof detail !== "object") return null;
  const refusal = refusalOf(error);
  return refusal
    ? new ObservationRefused(refusal.reason, refusal.observationUuid, 404)
    : null;
};

const OUT_OF_RANGE = "observed_at_out_of_range";

const outOfRangeOf = (error: unknown): ObservationRefused | null => {
  if (!error || typeof error !== "object" || !("detail" in error)) return null;
  const { detail } = error as { detail: unknown };
  if (typeof detail !== "string" || !detail.startsWith(`${OUT_OF_RANGE}:`))
    return null;
  return new ObservationRefused(
    OUT_OF_RANGE,
    detail.slice(OUT_OF_RANGE.length + 1).trim() || null,
    422,
  );
};

const toReceipt = (r: Wire["ObservationsOut"]): ObservationsReceipt => ({
  roomId: r.room_uuid,
  updatedAt: r.updated_at,
  occupantsChangedEventUuid: r.occupants_changed_event_uuid,
  results: r.results.map((x) => ({
    observationUuid: x.observation_uuid,
    status: x.status,
    eventUuid: x.event_uuid,
  })),
  occupants: r.occupants.map(toOccupant),
});

/** Mirrors brian `modules/perception/clinic_roster.py` `ClinicRosterToday`; change both together. */
export type ClinicRosterToday = Wire["ClinicRosterToday"];
export type HouseholdOnRoster = Wire["HouseholdOnRoster"];
export type HouseholdMember = Wire["HouseholdMember"];

export const perceptionClient = {
  clinicRoster: (signal?: AbortSignal): Promise<ClinicRosterToday> =>
    request(() => apiClient.GET("/perception/clinic/roster", { signal })),

  phases: (signal?: AbortSignal): Promise<RoomPhase[]> =>
    request(() => apiClient.GET("/perception/rooms/phases", { signal })).then(
      (rows) =>
        rows.map((r) => ({
          roomId: r.room_uuid,
          roomName: r.room_name,
          kind: r.kind,
          phase: r.phase,
          since: r.since,
          occupants: r.occupants.map(toOccupant),
          counts: r.counts,
        })),
    ),

  /** Leadership only (brian `LeadershipAuth`). Null when the room has no still yet (404). */
  still: (roomId: string, signal?: AbortSignal): Promise<RoomStill | null> =>
    request(() =>
      apiClient.GET("/perception/rooms/{room_uuid}/still", {
        params: { path: { room_uuid: roomId } },
        signal,
      }),
    ).then(toRoomStill, (error: unknown) => {
      if (error instanceof ClinicAuthError && error.status === 404) return null;
      throw error;
    }),

  /** Leadership only. Oldest first; each URL is presigned for about five minutes. */
  recentStills: (
    roomId: string,
    signal?: AbortSignal,
  ): Promise<RecentStill[]> =>
    request(() =>
      apiClient.GET("/perception/rooms/{room_uuid}/recent-stills", {
        params: { path: { room_uuid: roomId } },
        signal,
      }),
    ).then((rows) => {
      const signedAt = Date.now();
      return rows.map((s) => ({ ...toRoomStill(s), signedAt }));
    }),

  /** Leadership only; at most 5 minutes. Null when the room sent no video then (404). */
  video: (
    roomId: string,
    start: string,
    end: string,
    signal?: AbortSignal,
  ): Promise<RoomVideo | null> =>
    request(async () => {
      const result = await apiClient.GET(
        "/perception/rooms/{room_uuid}/video",
        {
          params: { path: { room_uuid: roomId }, query: { start, end } },
          parseAs: "blob",
          signal,
        },
      );
      const startedAt = result.response.headers.get("X-Video-Start");
      return {
        ...result,
        data:
          result.data && startedAt
            ? { mp4: result.data, startedAt }
            : undefined,
      };
    }).catch((error: unknown) => {
      if (error instanceof ClinicAuthError && error.status === 404) return null;
      throw error;
    }),

  /** Leadership only: its stills show clients. Null when the slot has left the roster (404). */
  slot: (
    roomId: string,
    slotId: string,
    signal?: AbortSignal,
  ): Promise<RoomSlot | null> =>
    request(() =>
      apiClient.GET("/perception/rooms/{room_uuid}/slots/{slot_id}", {
        params: { path: { room_uuid: roomId, slot_id: slotId } },
        signal,
      }),
    ).then(toSlot, (error: unknown) => {
      if (error instanceof ClinicAuthError && error.status === 404) return null;
      throw error;
    }),

  /** Leadership only. Newest first, the last few hours. */
  roomEvents: (roomId: string, signal?: AbortSignal): Promise<RoomEvent[]> =>
    request(() =>
      apiClient.GET("/perception/rooms/{room_uuid}/events", {
        params: { path: { room_uuid: roomId } },
        signal,
      }),
    ),

  /** Leadership only (brian `LeadershipAuth`). */
  groundTruth: (
    roomId: string,
    signal?: AbortSignal,
  ): Promise<RoomGroundTruth> =>
    request(() =>
      apiClient.GET("/perception/rooms/{room_uuid}/ground-truth", {
        params: { path: { room_uuid: roomId } },
        signal,
      }),
    ).then((r) => ({
      phases: r.phases,
      entries: r.entries.map((e) => ({
        id: e.uuid,
        recordedAt: e.recorded_at,
        phase: e.phase,
        text: e.text,
        door: e.door,
        authorName: e.author_name,
      })),
    })),

  /** brian times the entry at `body.at`, else when it arrives. */
  recordGroundTruth: (roomId: string, body: GroundTruthIn): Promise<void> =>
    request(() =>
      apiClient.POST("/perception/rooms/{room_uuid}/ground-truth", {
        params: { path: { room_uuid: roomId } },
        body,
      }),
    ).then(() => undefined),

  /** UserAuth. All observations commit in one transaction or none do; a 409 names the refused one when brian can. */
  recordObservations: async (
    roomId: string,
    body: ObservationsIn,
  ): Promise<ObservationsReceipt> => {
    let refusal: ObservationRefused | null = null;
    const out = await request(async () => {
      const result = await apiClient.POST(
        "/perception/rooms/{room_uuid}/observations",
        { params: { path: { room_uuid: roomId } }, body },
      );
      if (result.response.status === 409) refusal = refusalOf(result.error);
      if (result.response.status === 404) refusal = notFoundOf(result.error);
      if (result.response.status === 422) refusal = outOfRangeOf(result.error);
      return result;
    }).catch((error: unknown) => {
      throw refusal ?? error;
    });
    return toReceipt(out);
  },
};
